import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useVerifyTokenMutation,
  useMakeChangeMutation,
} from '@viome/viome-base/src/usecase/ForgotPassword/ForgotPasswordQuery';
import { parseQueryParams } from '@/utils/QueryParamsRoutes/queryParamsUtils';
import {
  RESET_PASSWORD_SUCCESS_PATH,
  RESET_PASSWORD_EXPIRED_PATH,
} from '@/constants/paths';

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one numeric character')
  .regex(
    /[!@#$%^&*(),.?":{}|<>]/,
    'Password must contain at least one special character',
  );

const resetPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Your passwords do not match. Please try again.',
    path: ['confirmPassword'],
  });

type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;

const useResetPassword = () => {
  const [isVerifying, setIsVerifying] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: verifyToken } = useVerifyTokenMutation();
  const { mutate: makeChange, isPending: isSubmitting } =
    useMakeChangeMutation();

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Extract query params
  const queryParams = parseQueryParams(location.search);
  const email = queryParams.email || '';
  const token = queryParams['reset-password-token'] || '';

  useEffect(() => {
    // Verify token on mount
    if (!email || !token) {
      navigate(RESET_PASSWORD_EXPIRED_PATH, {
        state: { email },
      });
      return;
    }

    verifyToken(
      { email, token },
      {
        onSuccess: () => {
          setIsVerifying(false);
        },
        onError: (error: Error) => {
          const errorMessage = error?.message || 'The link has expired';
          navigate(RESET_PASSWORD_EXPIRED_PATH, {
            state: { errorMessage, email },
          });
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token]);

  const onSubmit = (data: ResetPasswordForm) => {
    makeChange(
      {
        email,
        token,
        newPassword: data.password,
      },
      {
        onSuccess: () => {
          navigate(RESET_PASSWORD_SUCCESS_PATH);
        },
        onError: (error: Error) => {
          const errorMessage =
            error?.message ||
            'Error while resetting password. Please try again.';
          navigate(RESET_PASSWORD_EXPIRED_PATH, {
            state: { errorMessage, email },
          });
        },
      },
    );
  };

  const isFormValid = form.formState.isValid;

  return {
    form,
    onSubmit,
    isVerifying,
    isSubmitting,
    isFormValid,
  };
};

export default useResetPassword;
